import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, FileText, Calendar, Clock, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import api from '../../lib/api';
import { formatDate } from '../../lib/utils';

const serviceTypeLabels = {
  tourist_plan: 'Plan Touristique',
  insurance: 'Assurance Voyage',
  conciergerie: 'Conciergerie'
};

const urgencyColors = {
  normal: 'bg-green-500/20 text-green-400',
  urgent: 'bg-yellow-500/20 text-yellow-400',
  very_urgent: 'bg-orange-500/20 text-orange-400',
  immediate: 'bg-red-500/20 text-red-400'
};

const urgencyLabels = {
  normal: 'Normal',
  urgent: 'Urgent',
  very_urgent: 'Très Urgent',
  immediate: 'Immédiat'
};

export const ServiceRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/admin/service-requests');
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = 
      req.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.service?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || req.service_type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-requests-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Demandes de Services</h2>
        <p className="text-gray-400">{requests.length} demandes au total</p>
      </div>

      {/* Filters */}
      <Card className="bg-[#0F172A] border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white"
                data-testid="search-input"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Type de service" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10">
                <SelectItem value="all">Tous les types</SelectItem>
                {Object.entries(serviceTypeLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      <Card className="bg-[#0F172A] border-white/10">
        <CardContent className="p-0">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune demande trouvée</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {filteredRequests.map((req) => (
                <div 
                  key={req.id}
                  className="p-4 hover:bg-white/5 transition-colors cursor-pointer"
                  onClick={() => { setSelectedRequest(req); setShowDetail(true); }}
                  data-testid={`request-row-${req.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{req.full_name}</p>
                        <p className="text-sm text-gray-400">
                          {serviceTypeLabels[req.service_type] || req.service_type}
                          {req.service && ` - ${req.service}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {req.urgency && (
                        <Badge className={urgencyColors[req.urgency]}>
                          {urgencyLabels[req.urgency]}
                        </Badge>
                      )}
                      <span className="text-sm text-gray-500 hidden md:block">
                        {formatDate(req.created_at)}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-gray-400 hover:text-white"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-400" />
              </div>
              {serviceTypeLabels[selectedRequest?.service_type] || 'Demande'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Reçue le {selectedRequest && formatDate(selectedRequest.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Nom</p>
                  <p className="text-white">{selectedRequest.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Email</p>
                  <p className="text-white">{selectedRequest.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Téléphone</p>
                  <p className="text-white">{selectedRequest.phone}</p>
                </div>
                {selectedRequest.destination && (
                  <div>
                    <p className="text-sm text-gray-400">Destination</p>
                    <p className="text-white">{selectedRequest.destination}</p>
                  </div>
                )}
              </div>

              {selectedRequest.service && (
                <div>
                  <p className="text-sm text-gray-400">Service</p>
                  <p className="text-white">{selectedRequest.service}</p>
                </div>
              )}

              {selectedRequest.urgency && (
                <div>
                  <p className="text-sm text-gray-400">Urgence</p>
                  <Badge className={urgencyColors[selectedRequest.urgency]}>
                    {urgencyLabels[selectedRequest.urgency]}
                  </Badge>
                </div>
              )}

              {selectedRequest.additional_info && (
                <div>
                  <p className="text-sm text-gray-400">Informations supplémentaires</p>
                  <p className="text-white bg-white/5 p-3 rounded-lg mt-1">
                    {selectedRequest.additional_info}
                  </p>
                </div>
              )}

              {selectedRequest.delivery_method && (
                <div>
                  <p className="text-sm text-gray-400">Mode de réception</p>
                  <p className="text-white capitalize">{selectedRequest.delivery_method}</p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <a 
                  href={`mailto:${selectedRequest.email}`}
                  className="flex-1"
                >
                  <Button variant="outline" className="w-full border-white/10 text-white">
                    Répondre par Email
                  </Button>
                </a>
                <a 
                  href={`https://wa.me/${selectedRequest.phone?.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button className="w-full bg-green-500 hover:bg-green-600">
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceRequestsPage;
